import Raven from "raven-js";
import LocalSettings from "nexia_mobile/models/local_settings";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import PromiseRetrier from "nexia_mobile/PromiseRetrier";
import { ResourceMap } from "nexia_mobile/framework/resource";

const NUM_RETRIES = 15;
const RETRY_DELAY = 2000;

class EventStream {
  start(link) {
    if (this.streamingDisabled) return Promise.resolve();

    this._debug("EventStream.start called");

    this._paused = false;
    this._reconnectUrl = link.data.reconnect_href;
    return this._connectWithRetry(link.href);
  }

  pause() {
    this._debug("EventStream.pause called");
    this._paused = true;
  }

  resume() {
    this._debug("EventStream.resume called");
    this._paused = false;
    this.checkForReconnect();
  }

  end() {
    this._debug("EventStream.end called");
    if (!this._socket) return Promise.resolve();

    return new Promise((resolve) => {
      this._socket.onclose = resolve;
      this._socket.close();
      this._socket = null;
    });
  }

  checkForReconnect() {
    if (this.connected) return Promise.resolve();
    if (this.streamingDisabled) return Promise.resolve();
    if (this._paused) return Promise.resolve();
    if (this._connectionInProgress) return this._connectionInProgress;

    return this._reconnect(this._reconnectUrl);
  }

  get streamingDisabled() {
    return !LocalSettings.getEventStreamingEnabled();
  }

  get connected() {
    return Boolean(this._socket);
  }

  _connectWithRetry(wsUrl, reconnectUrl) {
    if (this._connectionInProgress) return this._connectionInProgress;

    this._connectionInProgress = new PromiseRetrier(
      () => this._initConnection(wsUrl, reconnectUrl),
      NUM_RETRIES,
      RETRY_DELAY
    ).run();

    const nullConnectionInProgress = () => (this._connectionInProgress = null);
    this._connectionInProgress.then(
      nullConnectionInProgress,
      nullConnectionInProgress
    );

    return this._connectionInProgress;
  }

  _initConnection(wsUrl) {
    if (this._socket) {
      this._debug("closing open socket before opening new");
      this.end();
    }

    return this._createSocket(wsUrl).then((socket) => {
      this._socket = socket;
      socket.onmessage = (event) => {
        this._debug("Message received");
        this._onMessage(event);
      };
      socket.onclose = (event) => {
        this._debug(`WebSocket closed: ${event}`);
        this._socket = null;
        this.checkForReconnect();
      };
    });
  }

  _createSocket(wsUrl) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(wsUrl);
      this._debug("WebSocket created");
      socket.onerror = (event) => {
        const error = new Error("WebSocket Connection Failed");
        error.event = event;
        this._debug(error.message);
        reject(error);
      };
      socket.onopen = () => {
        socket.onerror = null;
        this._debug("WebSocket opened");
        resolve(socket);
      };
    });
  }

  _reconnect(reconnectUrl) {
    return new Promise((resolve, reject) => {
      if (!reconnectUrl) {
        reject(
          new Error("Attempted to start reconnect but had no reconnect URL")
        );
        return;
      }

      this._debug("Calling Nexia to reserve EventStream");

      $.ajax({
        url: reconnectUrl,
        method: "POST",
        dataType: "json",
        error: (_jqxhr, textStatus, errorThrown) =>
          reject(new Error(`${textStatus} - ${errorThrown}`)),
        success: resolve,
      });
    }).then((data) => {
      this._debug("Attempting WebSocket reconnect");
      this.start(data.result);
    });
  }

  _onMessage(event) {
    try {
      this._handleMessage(event.data);
    } catch (error) {
      Raven.captureException(
        new Error(
          `Failed to parse message from Event Stream: ${error.message}`
        ),
        {
          extra: {
            data: event.data,
          },
        }
      );
    }
  }

  _handleMessage(json) {
    const message = JSON.parse(json);
    if (message.event_type !== "update") return;

    const href = message.serializable._links.self.href;
    const model = ResourceMap.findByHref(href);
    if (model) {
      model.set(model.parse(message.serializable));
    }
  }

  _debug(message) {
    if (!LocalSettings.getDebugStreaming()) return;

    Dispatch("SHOW_TOAST", `ES: ${message}`);
  }
}

if (EventStream.singleton == null) {
  EventStream.singleton = new EventStream();
}
export default EventStream.singleton;



// WEBPACK FOOTER //
// ./src/event_stream.js